// TEMPORARY: fake concession data for testing the UI with a live game.
// On with the dashboard-wide TEST_MODE (src/testMode.js), or by itself with DUMMY_ENABLED below.
// It only ever runs under `npm start` (development); production builds can never enable it.
// Remove this file (and its uses in ConcessionLocationController.js, ConcessionOverview.js,
// LocationsController.js and the banner in ConcessionLocation.js) when done.
//
// Shares the test game (gates 2 hours, start 1 hour before page load, three past games) and
// sends a reading per section every 30 seconds up to "now". Each section steps through the
// edge cases below in 12-minute windows (offset per section), on top of a realistic crowd curve.
import { getQueuesAPI } from '../../util.js';
import {
  IS_DEV, USE_TEST_MODE, noise, testSchedule, gameContext, readingTimes, localStamp, edgeCase, edgeProgress, crowdCurve,
} from '../../testMode';

const DUMMY_ENABLED = false;

export const USE_DUMMY_CONCESSION_DATA = IS_DEV && (DUMMY_ENABLED || USE_TEST_MODE);

// Queue minutes: whole numbers, never below 1, like the backend, people counts and overflow, per edge case
const CASES = [
  'normal',
  'exact10', // 10 min: red
  'nine', // 9 min: the highest green
  'overflowLow', // overflow flagged while the queue reads short
  'overflowHigh', // overflow and a long queue
  'spike', // very long queue, very busy
  'minimum', // 1 min, almost empty
  'rising', // climbs from green to red over the window
  'falling', // drops from red to green over the window
];

const reading = (ctx, sec, sectionIndex) => {
  const minute = Math.floor(sec / 60);
  const c = edgeCase(CASES, sectionIndex, minute);
  if (c === 'stale') return null;
  const p = edgeProgress(minute);
  const n = noise(sec / 30 + sectionIndex * 977 + ctx.seed);
  const crowd = crowdCurve(ctx, minute) * ctx.scale;
  const sectionScale = 0.7 + (sectionIndex % 4) * 0.2;

  let queueTime = 1 + crowd * 12 * sectionScale + n * 3;
  let overflow = 0;
  let people = 2 + crowd * 40 * sectionScale + n * 8;
  switch (c) {
    case 'exact10': queueTime = 10; break;
    case 'nine': queueTime = 9; break;
    case 'overflowLow': queueTime = 6 + n; overflow = 1; break;
    case 'overflowHigh': queueTime = 15 + n * 3; overflow = 1; people = 55 + n * 10; break;
    case 'spike': queueTime = 24 + n * 6; people = 70 + n * 15; break;
    case 'minimum': queueTime = 1; people = n < 0.5 ? 0 : 1; break;
    case 'rising': queueTime = 3 + p * 1.2; people = 10 + p * 4; break;
    case 'falling': queueTime = 16 - p * 1.2; people = 55 - p * 4; break;
    default: break;
  }
  return {
    queueTime: Math.max(1, Math.round(queueTime)),
    overflow,
    peopleCount: Math.max(0, Math.round(people)),
    // Seconds per transaction: 20s quick to 5+ min slow
    transactionTime: Math.round(c === 'spike' ? 280 + n * 60 : c === 'minimum' ? 20 + n * 10 : 45 + crowd * 60 + n * 40),
  };
};

// 'get-concession-location-daily-data-v2': [{ sectionId: [{ time, queueTime, transactionTime, peopleCount, overflow }] }]
const buildDailyData = (currentLocation, dateStr) => {
  const ctx = gameContext(currentLocation.timeZone, dateStr);
  const sections = currentLocation.sections || [];
  if (!ctx) return sections.map((s) => ({ [s.sectionId]: [] }));
  return sections.map((section, i) => {
    const points = [];
    readingTimes(ctx, ctx.start - 120).forEach((sec) => {
      const r = reading(ctx, sec, i);
      // Venue-local clock times, like the backend
      if (r) points.push({ time: localStamp(dateStr, sec), ...r });
    });
    return { [section.sectionId]: points };
  });
};

// 'get-concession-location-current-data-dashboard(-v2)' (Locations card): each section's latest
// reading; a section with nothing in the last minute is inactive and reads 0, like the backend
const buildCurrent = (currentLocation) => {
  const live = testSchedule(currentLocation.timeZone)[1].dates[0];
  const ctx = gameContext(currentLocation.timeZone, live.date);
  return (currentLocation.sections || []).map((section, i) => {
    let latest = null;
    for (let sec = ctx.endSec; sec > ctx.endSec - 60 && !latest; sec -= 30) {
      const aligned = sec - (sec % 30);
      latest = reading(ctx, aligned, i);
    }
    return {
      section: section.sectionId,
      queueTime: latest ? Math.round(latest.queueTime) : 0,
      peopleCount: latest ? latest.peopleCount : 0,
      sensorActive: !!latest,
    };
  });
};

// Drop-in replacement for getQueuesAPI. Serves fake schedule and data, defers everything else.
export const getConcessionAPI = (currentLocation) => (route, body, callback, token) => {
  if (!USE_DUMMY_CONCESSION_DATA || !currentLocation) return getQueuesAPI(route, body, callback, token);
  const respond = (data) => setTimeout(() => callback(data), 150);
  switch (route) {
    case 'get-location-current-schedule':
      return respond(testSchedule(currentLocation.timeZone));
    case 'get-concession-location-daily-data-v2':
      return respond(buildDailyData(currentLocation, body.date));
    case 'get-concession-location-current-data-dashboard':
    case 'get-concession-location-current-data-dashboard-v2':
      return respond(buildCurrent(currentLocation));
    default:
      return getQueuesAPI(route, body, callback, token);
  }
};
